const coppermind = (page) => `https://coppermind.net/wiki/${page}`;
const symbolPath = (system, name) =>
  `/symbols/${system}/${name.toLowerCase().replaceAll(' ', '-')}.svg`;

export const characterTypes = [
  {
    id: 'type-misting',
    name: 'Misting',
    subtitle: 'One Allomantic power',
    world: 'scadrial',
    system: 'character',
    group: 'character',
    images: [symbolPath('allomancy', 'steel')],
    summary: 'A focused Allomancer who can burn one metal.',
    details:
      'When this type is in the pool, a later randomizer can choose one ability from your selected Allomantic metals.',
    tags: ['scadrial', 'allomancy', 'metalborn'],
    sourceUrl: coppermind('Allomancy#Types_of_Allomancers'),
  },
  {
    id: 'type-mistborn',
    name: 'Mistborn',
    subtitle: 'The full Allomantic suite',
    world: 'scadrial',
    system: 'character',
    group: 'character',
    images: [symbolPath('allomancy', 'atium')],
    summary: 'An Allomancer with access to every standard Allomantic metal.',
    details:
      'Mistborn do not need an individual metal chosen to use their core suite. Your selected metal cards can still shape other character types in the pool.',
    tags: ['scadrial', 'allomancy', 'metalborn'],
    sourceUrl: coppermind('Mistborn'),
  },
  {
    id: 'type-ferring',
    name: 'Ferring',
    subtitle: 'One Feruchemical power',
    world: 'scadrial',
    system: 'character',
    group: 'character',
    images: [symbolPath('feruchemy', 'steel')],
    summary: 'A focused Feruchemist who stores and taps one attribute.',
    details:
      'When this type is in the pool, a later randomizer can choose one ability from your selected Feruchemical metals.',
    tags: ['scadrial', 'feruchemy', 'metalborn'],
    sourceUrl: coppermind('Feruchemy#Types_of_Feruchemists'),
  },
  {
    id: 'type-full-feruchemist',
    name: 'Full Feruchemist',
    subtitle: 'The full Feruchemical suite',
    world: 'scadrial',
    system: 'character',
    group: 'character',
    images: [symbolPath('feruchemy', 'atium')],
    summary: 'A Feruchemist able to store and tap every standard attribute.',
    details:
      'Full Feruchemists can use every standard metalmind. Individual Feruchemical cards remain useful for Ferrings and Twinborn in the same possibility pool.',
    tags: ['scadrial', 'feruchemy', 'metalborn'],
    sourceUrl: coppermind('Feruchemy#Types_of_Feruchemists'),
  },
  {
    id: 'type-twinborn',
    name: 'Twinborn',
    subtitle: 'One power from each Metallic Art',
    world: 'scadrial',
    system: 'character',
    group: 'character',
    images: [symbolPath('allomancy', 'steel'), symbolPath('feruchemy', 'iron')],
    imageLabels: ['A', 'F'],
    summary: 'A Metalborn with one Allomantic and one Feruchemical ability.',
    details:
      'A later randomizer can draw one metal from each selected pool. Matching metals create a Compounder; different metals may create a Resonance.',
    tags: ['scadrial', 'allomancy', 'feruchemy', 'metalborn', 'compounder'],
    sourceUrl: coppermind('Twinborn'),
  },
  {
    id: 'type-knight-radiant',
    name: 'Knight Radiant',
    subtitle: 'One selected Radiant Order',
    world: 'roshar',
    system: 'character',
    group: 'character',
    images: [symbolPath('orders', 'windrunners'), symbolPath('orders', 'lightweavers')],
    summary: 'A Surgebinder bonded to a spren and aligned with a Radiant Order.',
    details:
      'When this type is in the pool, a later randomizer can choose one of your selected Radiant Orders and grant its paired Surges. Oath level remains deferred until scenario generation.',
    tags: ['roshar', 'surgebinding', 'knights radiant', 'spren'],
    sourceUrl: coppermind('Knights_Radiant'),
  },
  {
    id: 'type-hemalurgy',
    name: 'Hemalurgic spike',
    subtitle: 'Deferred',
    world: 'scadrial',
    system: 'character',
    group: 'character',
    images: [symbolPath('allomancy', 'iron')],
    summary: 'Hemalurgy is intentionally paused for a later milestone.',
    details:
      'The design document calls for a separate lore review before Hemalurgic powers enter the randomization pool.',
    tags: ['scadrial', 'hemalurgy', 'deferred'],
    sourceUrl: coppermind('Hemalurgy'),
    disabled: true,
  },
];

const metals = [
  {
    name: 'Iron',
    allomanticTitle: 'Lurcher',
    allomanticGroup: 'Physical',
    allomanticSummary: 'Pulls nearby metal toward the user.',
    allomanticDetails:
      'Iron is an external, physical Pulling metal. Skilled users can sense nearby metal and move themselves by Pulling against sufficiently heavy or anchored objects.',
    feruchemicalTitle: 'Skimmer',
    feruchemicalGroup: 'Physical',
    feruchemicalSummary: 'Stores physical mass.',
    feruchemicalDetails:
      'An ironmind lets a Feruchemist become lighter while storing mass and heavier while tapping it, changing momentum and how strongly other forces move them.',
  },
  {
    name: 'Steel',
    allomanticTitle: 'Coinshot',
    allomanticGroup: 'Physical',
    allomanticSummary: 'Pushes nearby metal away from the user.',
    allomanticDetails:
      'Steel is an external, physical Pushing metal. Coinshots launch metal projectiles and can move through the air by Pushing against metal below them.',
    feruchemicalTitle: 'Steelrunner',
    feruchemicalGroup: 'Physical',
    feruchemicalSummary: 'Stores physical speed.',
    feruchemicalDetails:
      'A steelmind stores physical speed, slowing the Feruchemist while filling and granting bursts of extraordinary movement when tapped.',
  },
  {
    name: 'Tin',
    allomanticTitle: 'Tineye',
    allomanticGroup: 'Physical',
    allomanticSummary: 'Heightens the physical senses.',
    allomanticDetails:
      'Tin is an internal, physical Pulling metal that sharpens sight, hearing, touch, smell, and taste, including pain and other unwanted sensations.',
    feruchemicalTitle: 'Windwhisperer',
    feruchemicalGroup: 'Physical',
    feruchemicalSummary: 'Stores individual senses.',
    feruchemicalDetails:
      'Tinminds can store senses separately. The user experiences a diminished sense while storing and an enhanced version of that sense while tapping.',
  },
  {
    name: 'Pewter',
    allomanticTitle: 'Thug',
    allomanticGroup: 'Physical',
    allomanticSummary: 'Enhances strength, balance, speed, and endurance.',
    allomanticDetails:
      'Pewter is an internal, physical Pushing metal. It reinforces the body, improves coordination, and helps the user endure exertion and injury.',
    feruchemicalTitle: 'Brute',
    feruchemicalGroup: 'Physical',
    feruchemicalSummary: 'Stores physical strength.',
    feruchemicalDetails:
      'A pewtermind makes the Feruchemist weaker while filling it and grants increased muscle mass and strength when tapped.',
  },
  {
    name: 'Zinc',
    allomanticTitle: 'Rioter',
    allomanticGroup: 'Mental',
    allomanticSummary: 'Inflames selected emotions in other people.',
    allomanticDetails:
      'Zinc is an external, mental Pulling metal. A Rioter strengthens emotions that are already present rather than creating feelings from nothing.',
    feruchemicalTitle: 'Sparker',
    feruchemicalGroup: 'Cognitive',
    feruchemicalSummary: 'Stores mental speed.',
    feruchemicalDetails:
      'A zincmind slows thought while storing and allows extremely rapid thinking, calculation, and decision-making while tapped.',
  },
  {
    name: 'Brass',
    allomanticTitle: 'Soother',
    allomanticGroup: 'Mental',
    allomanticSummary: 'Dampens selected emotions in other people.',
    allomanticDetails:
      'Brass is an external, mental Pushing metal. A Soother suppresses chosen emotions and can subtly influence how a target responds.',
    feruchemicalTitle: 'Firesoul',
    feruchemicalGroup: 'Cognitive',
    feruchemicalSummary: 'Stores warmth.',
    feruchemicalDetails:
      'A brassmind stores body heat. The Feruchemist grows colder while filling it and can warm themselves dramatically when tapping.',
  },
  {
    name: 'Copper',
    allomanticTitle: 'Smoker',
    allomanticGroup: 'Mental',
    allomanticSummary: 'Hides nearby Allomantic pulses.',
    allomanticDetails:
      'Copper is an internal, mental Pulling metal. It creates a coppercloud that conceals Allomancy from Seekers and protects the user from emotional Allomancy.',
    feruchemicalTitle: 'Archivist',
    feruchemicalGroup: 'Cognitive',
    feruchemicalSummary: 'Stores memories.',
    feruchemicalDetails:
      'A coppermind stores specific memories outside the mind. They can later be recalled with exceptional clarity, though memories degrade after retrieval.',
  },
  {
    name: 'Bronze',
    allomanticTitle: 'Seeker',
    allomanticGroup: 'Mental',
    allomanticSummary: 'Detects the pulses of active Allomancy.',
    allomanticDetails:
      'Bronze is an internal, mental Pushing metal. A trained Seeker can identify the location, rhythm, and type of nearby Allomantic activity.',
    feruchemicalTitle: 'Sentry',
    feruchemicalGroup: 'Cognitive',
    feruchemicalSummary: 'Stores wakefulness.',
    feruchemicalDetails:
      'A bronzemind makes the Feruchemist drowsy while filling and allows them to remain alert without sleep while tapping.',
  },
  {
    name: 'Aluminum',
    allomanticTitle: 'Aluminum Gnat',
    allomanticGroup: 'Enhancement',
    allomanticSummary: 'Instantly wipes the user\'s metal reserves.',
    allomanticDetails:
      'Aluminum is an internal enhancement Pulling metal. Burning it removes the user\'s remaining Allomantic reserves and can clear some unwanted Invested effects.',
    feruchemicalTitle: 'Trueself',
    feruchemicalGroup: 'Spiritual',
    feruchemicalSummary: 'Stores Identity.',
    feruchemicalDetails:
      'An aluminummind stores spiritual Identity, allowing advanced interactions with metalminds and other Invested arts while that Identity is suppressed.',
  },
  {
    name: 'Duralumin',
    allomanticTitle: 'Duralumin Gnat',
    allomanticGroup: 'Enhancement',
    allomanticSummary: 'Releases another burning metal in one amplified burst.',
    allomanticDetails:
      'Duralumin is an internal enhancement Pushing metal. It consumes the current reserve of another active metal at once, creating a brief and powerful effect.',
    feruchemicalTitle: 'Connector',
    feruchemicalGroup: 'Spiritual',
    feruchemicalSummary: 'Stores Connection.',
    feruchemicalDetails:
      'A duraluminmind stores spiritual Connection to people, places, and cultures, with effects that can include social affinity and language understanding.',
  },
  {
    name: 'Chromium',
    allomanticTitle: 'Leecher',
    allomanticGroup: 'Enhancement',
    allomanticSummary: 'Wipes another Allomancer\'s metal reserves by touch.',
    allomanticDetails:
      'Chromium is an external enhancement Pulling metal. A Leecher who touches a target can rapidly destroy their usable Allomantic reserves.',
    feruchemicalTitle: 'Spinner',
    feruchemicalGroup: 'Spiritual',
    feruchemicalSummary: 'Stores Fortune.',
    feruchemicalDetails:
      'A chromiummind stores Fortune, an uncertain Spiritual attribute related to being guided toward useful outcomes rather than simple probability control.',
  },
  {
    name: 'Nicrosil',
    allomanticTitle: 'Nicroburst',
    allomanticGroup: 'Enhancement',
    allomanticSummary: 'Amplifies another Allomancer\'s active metal by touch.',
    allomanticDetails:
      'Nicrosil is an external enhancement Pushing metal. A Nicroburst forces a touched target\'s active metal to release its reserve in an amplified burst.',
    feruchemicalTitle: 'Soulbearer',
    feruchemicalGroup: 'Spiritual',
    feruchemicalSummary: 'Stores Investiture.',
    feruchemicalDetails:
      'A nicrosilmind stores Investiture itself, including the ability to use an Invested art under the right circumstances.',
  },
  {
    name: 'Gold',
    allomanticTitle: 'Augur',
    allomanticGroup: 'Temporal',
    allomanticSummary: 'Reveals possible versions of the user\'s past.',
    allomanticDetails:
      'Gold is an internal temporal Pulling metal. It shows visions of alternate past selves shaped by choices the user might have made.',
    feruchemicalTitle: 'Bloodmaker',
    feruchemicalGroup: 'Hybrid',
    feruchemicalSummary: 'Stores health.',
    feruchemicalDetails:
      'A goldmind weakens health while filling and fuels rapid healing while tapped, from ordinary recovery to surviving severe injury if enough health is stored.',
  },
  {
    name: 'Electrum',
    allomanticTitle: 'Oracle',
    allomanticGroup: 'Temporal',
    allomanticSummary: 'Shows several versions of the user\'s immediate future.',
    allomanticDetails:
      'Electrum is an internal temporal Pushing metal. Its future shadows help the user react to danger and interfere with Atium-based prediction.',
    feruchemicalTitle: 'Pinnacle',
    feruchemicalGroup: 'Hybrid',
    feruchemicalSummary: 'Stores determination.',
    feruchemicalDetails:
      'An electrummind reduces motivation while filling and supplies drive, resolve, and resistance to despair while tapped.',
  },
  {
    name: 'Cadmium',
    allomanticTitle: 'Pulser',
    allomanticGroup: 'Temporal',
    allomanticSummary: 'Creates a bubble where time passes more slowly.',
    allomanticDetails:
      'Cadmium is an external temporal Pulling metal. Time inside its fixed bubble moves slowly compared with the world beyond its boundary.',
    feruchemicalTitle: 'Gasper',
    feruchemicalGroup: 'Hybrid',
    feruchemicalSummary: 'Stores breath.',
    feruchemicalDetails:
      'A cadmiummind stores breath, leaving the Feruchemist short of air while filling and letting them function without breathing while tapping.',
  },
  {
    name: 'Bendalloy',
    allomanticTitle: 'Slider',
    allomanticGroup: 'Temporal',
    allomanticSummary: 'Creates a bubble where time passes more quickly.',
    allomanticDetails:
      'Bendalloy is an external temporal Pushing metal. People inside its fixed bubble can act rapidly while relatively little time passes outside.',
    feruchemicalTitle: 'Subsumer',
    feruchemicalGroup: 'Hybrid',
    feruchemicalSummary: 'Stores energy and nutrition.',
    feruchemicalDetails:
      'A bendalloymind stores calories and fluids, allowing the Feruchemist to bank nourishment and later function without eating or drinking.',
  },
  {
    name: 'Atium',
    allomanticTitle: 'Atium burner',
    allomanticGroup: 'God metal',
    allomanticSummary: 'Reveals the immediate future of nearby people and objects.',
    allomanticDetails:
      'Atium is Ruin\'s God Metal and sits outside the modern base sixteen. Its familiar Allomantic effect presents future shadows and enhanced mental processing.',
    feruchemicalTitle: 'Atium Feruchemist',
    feruchemicalGroup: 'God metal',
    feruchemicalSummary: 'Stores youth.',
    feruchemicalDetails:
      'An atium metalmind stores youth, aging the Feruchemist while filling and making them physically younger while tapping. Atium is outside the base sixteen.',
  },
];

export const allomancyCards = metals.map((metal) => ({
  id: `allomancy-${metal.name.toLowerCase()}`,
  name: metal.name,
  subtitle: metal.allomanticTitle,
  world: 'scadrial',
  system: 'allomancy',
  group: 'allomancy',
  images: [symbolPath('allomancy', metal.name)],
  category: metal.allomanticGroup,
  summary: metal.allomanticSummary,
  details: metal.allomanticDetails,
  tags: ['scadrial', 'allomancy', metal.allomanticTitle, metal.allomanticGroup],
  sourceUrl: coppermind(metal.name),
}));

export const feruchemyCards = metals.map((metal) => ({
  id: `feruchemy-${metal.name.toLowerCase()}`,
  name: metal.name,
  subtitle: metal.feruchemicalTitle,
  world: 'scadrial',
  system: 'feruchemy',
  group: 'feruchemy',
  images: [symbolPath('feruchemy', metal.name)],
  category: metal.feruchemicalGroup,
  summary: metal.feruchemicalSummary,
  details: metal.feruchemicalDetails,
  tags: ['scadrial', 'feruchemy', metal.feruchemicalTitle, metal.feruchemicalGroup],
  sourceUrl: coppermind(metal.name),
}));

const surges = [
  {
    name: 'Adhesion',
    subtitle: 'Pressure and Connection',
    summary: 'Binds surfaces and manipulates pressure, vacuum, and spiritual Connection.',
    details:
      'Adhesion can join objects through pressure and vacuum. Its deeper expression, especially for Bondsmiths, works with bonds, oaths, and Connection.',
    orders: ['Windrunners', 'Bondsmiths'],
  },
  {
    name: 'Gravitation',
    subtitle: 'Gravitational attraction',
    summary: 'Changes the direction and strength of gravity acting on a target.',
    details:
      'Gravitation creates Lashings that redirect a person or object toward a chosen direction, enabling flight-like movement and powerful directional force.',
    orders: ['Windrunners', 'Skybreakers'],
  },
  {
    name: 'Division',
    subtitle: 'Decay and destruction',
    summary: 'Breaks molecular bonds to burn, degrade, or destroy matter.',
    details:
      'Division accelerates destructive processes in touched material. Its use is dangerous and is approached differently by its two associated Orders.',
    orders: ['Skybreakers', 'Dustbringers'],
  },
  {
    name: 'Abrasion',
    subtitle: 'Friction',
    summary: 'Raises or lowers friction on people and surfaces.',
    details:
      'Abrasion can make movement nearly frictionless or make surfaces grip strongly, supporting rapid gliding and precise control of momentum.',
    orders: ['Dustbringers', 'Edgedancers'],
  },
  {
    name: 'Progression',
    subtitle: 'Growth and healing',
    summary: 'Accelerates natural growth and restores living bodies through Regrowth.',
    details:
      'Progression manipulates growth in living things. Regrowth heals wounds according to a person\'s spiritual ideal, while plants can be rapidly cultivated.',
    orders: ['Edgedancers', 'Truthwatchers'],
  },
  {
    name: 'Illumination',
    subtitle: 'Light and sound',
    summary: 'Shapes light and sound into convincing illusions.',
    details:
      'Illumination creates visual and auditory constructs. Its expression varies by Order and can combine observation, art, memory, and pattern recognition.',
    orders: ['Truthwatchers', 'Lightweavers'],
  },
  {
    name: 'Transformation',
    subtitle: 'Soulcasting',
    summary: 'Transforms one substance into another through Soulcasting.',
    details:
      'Transformation persuades the essence of matter to take a new form, often by interacting with its Cognitive aspect in Shadesmar.',
    orders: ['Lightweavers', 'Elsecallers'],
  },
  {
    name: 'Transportation',
    subtitle: 'Realmatic transition',
    summary: 'Moves between the Physical and Cognitive Realms.',
    details:
      'Transportation allows transitions into Shadesmar and may enable other forms of movement across distance, depending on the user and circumstance.',
    orders: ['Elsecallers', 'Willshapers'],
  },
  {
    name: 'Cohesion',
    subtitle: 'Shape solid matter',
    summary: 'Softens solid material so it can be reshaped by touch.',
    details:
      'Cohesion changes how rigid materials behave without transforming their substance, letting a Surgebinder sculpt stone and similar matter.',
    orders: ['Willshapers', 'Stonewards'],
  },
  {
    name: 'Tension',
    subtitle: 'Stiffness and rigidity',
    summary: 'Makes flexible material rigid or alters how strongly matter holds its shape.',
    details:
      'Tension changes the stiffness of material and supports broader feats of structural manipulation, particularly when paired with an Order\'s other Surge.',
    orders: ['Stonewards', 'Bondsmiths'],
  },
];

export const surgeCards = surges.map((surge) => ({
  id: `surge-${surge.name.toLowerCase()}`,
  name: surge.name,
  subtitle: surge.subtitle,
  world: 'roshar',
  system: 'surgebinding',
  group: 'surges',
  images: [symbolPath('surges', surge.name)],
  category: surge.orders.join(' + '),
  summary: surge.summary,
  details: surge.details,
  tags: ['roshar', 'surgebinding', ...surge.orders],
  sourceUrl: coppermind(surge.name),
}));

const orders = [
  {
    name: 'Windrunners',
    spren: 'Honorspren',
    surges: ['Adhesion', 'Gravitation'],
    focus: 'Protection and leadership',
    summary: 'Protectors and leaders who bind Adhesion with Gravitation.',
  },
  {
    name: 'Skybreakers',
    spren: 'Highspren',
    surges: ['Gravitation', 'Division'],
    focus: 'Justice and principled law',
    summary: 'Seekers of justice who combine aerial movement with destructive Division.',
  },
  {
    name: 'Dustbringers',
    spren: 'Ashspren',
    surges: ['Division', 'Abrasion'],
    focus: 'Self-mastery and responsibility',
    summary: 'Also called Releasers, they pursue disciplined control over dangerous power.',
  },
  {
    name: 'Edgedancers',
    spren: 'Cultivationspren',
    surges: ['Abrasion', 'Progression'],
    focus: 'Remembering and listening',
    summary: 'Graceful healers who remember people that others overlook.',
  },
  {
    name: 'Truthwatchers',
    spren: 'Mistspren',
    surges: ['Progression', 'Illumination'],
    focus: 'Truth and knowledge',
    summary: 'Scholars and healers concerned with understanding and sharing truth.',
  },
  {
    name: 'Lightweavers',
    spren: 'Cryptics',
    surges: ['Illumination', 'Transformation'],
    focus: 'Creative truth and self-awareness',
    summary: 'Artists and spies who shape light, sound, and matter while confronting personal truths.',
  },
  {
    name: 'Elsecallers',
    spren: 'Inkspren',
    surges: ['Transformation', 'Transportation'],
    focus: 'Wisdom and reaching potential',
    summary: 'Deliberate scholars with strong command of Soulcasting and Shadesmar travel.',
  },
  {
    name: 'Willshapers',
    spren: 'Lightspren',
    surges: ['Transportation', 'Cohesion'],
    focus: 'Freedom and exploration',
    summary: 'Adventurous builders who value freedom, travel, and self-determination.',
  },
  {
    name: 'Stonewards',
    spren: 'Peakspren',
    surges: ['Cohesion', 'Tension'],
    focus: 'Dependability and teamwork',
    summary: 'Steadfast team players who reshape stone and stand where they are needed.',
  },
  {
    name: 'Bondsmiths',
    spren: 'Unique great spren',
    surges: ['Tension', 'Adhesion'],
    focus: 'Unity and Connection',
    summary: 'Rare leaders who unite people and manipulate bonds at an exceptional scale.',
  },
];

export const radiantOrderCards = orders.map((order) => ({
  id: `order-${order.name.toLowerCase()}`,
  name: order.name,
  subtitle: order.surges.join(' + '),
  world: 'roshar',
  system: 'radiant-orders',
  group: 'orders',
  images: [symbolPath('orders', order.name)],
  category: order.focus,
  summary: order.summary,
  details: `${order.name} bond with ${order.spren}. Their profile is shaped by ${order.focus.toLowerCase()}. Oath level is intentionally deferred until scenario generation.`,
  tags: ['roshar', 'knights radiant', order.spren, order.focus, ...order.surges],
  sourceUrl: coppermind(`Order_of_${order.name}`),
}));

export const catalogueSections = [
  {
    id: 'character',
    title: 'Character types',
    eyebrow: 'Character presets',
    description:
      'Choose every kind of Invested character that may appear. Power and Order cards below define the pools used by focused character types.',
    cards: characterTypes,
  },
  {
    id: 'allomancy',
    title: 'Allomancy',
    eyebrow: 'Scadrial',
    description: 'Burn metal to draw an associated power from Preservation.',
    cards: allomancyCards,
  },
  {
    id: 'feruchemy',
    title: 'Feruchemy',
    eyebrow: 'Scadrial',
    description: 'Store an attribute in a metalmind, then tap it later.',
    cards: feruchemyCards,
  },
  {
    id: 'surges',
    title: 'Surges',
    eyebrow: 'Roshar',
    description: 'The ten fundamental forces manipulated through Surgebinding.',
    cards: surgeCards,
  },
  {
    id: 'orders',
    title: 'Knights Radiant',
    eyebrow: 'Roshar',
    description: 'Each Order bonds a type of spren and grants access to two adjacent Surges.',
    cards: radiantOrderCards,
  },
];

export const allCards = catalogueSections.flatMap((section) => section.cards);
export const selectableCards = allCards.filter((card) => !card.disabled);
export const cardById = Object.fromEntries(allCards.map((card) => [card.id, card]));

export const groupLabels = {
  character: 'Character types',
  allomancy: 'Allomancy',
  feruchemy: 'Feruchemy',
  surges: 'Surges',
  orders: 'Radiant Orders',
};

export const sourceLinks = [
  { label: 'Allomancy', url: coppermind('Allomancy') },
  { label: 'Feruchemy', url: coppermind('Feruchemy') },
  { label: 'Surgebinding', url: coppermind('Surgebinding') },
  { label: 'Knights Radiant', url: coppermind('Knights_Radiant') },
];
